import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

export default function AuditList() {
  const [audits, setAudits] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ site_name: "", target_url: "", context: "", sector: "clínica dental" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => api.get("/audits").then((r) => setAudits(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/audits", form);
      toast.success("Auditoría creada");
      setCreating(false);
      setForm({ site_name: "", target_url: "", context: "", sector: "clínica dental" });
      load();
    } catch { toast.error("Error creando auditoría"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="audit-list-page" className="p-8 lg:p-10">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--audit-muted))]">Panel de auditorías</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Auditorías</h1>
        </div>
        <button
          onClick={() => setCreating((v) => !v)}
          data-testid="new-audit-btn"
          className="inline-flex items-center gap-2 rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
        >
          <Plus size={16} /> Nueva auditoría
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} data-testid="new-audit-form" className="mt-6 rounded-md border border-[hsl(var(--audit-border))] bg-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nombre del sitio" required>
              <input required data-testid="audit-site-name" value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} className="input-a" />
            </Field>
            <Field label="URL objetivo" required>
              <input required type="url" placeholder="https://" data-testid="audit-target-url" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} className="input-a" />
            </Field>
            <Field label="Sector">
              <input data-testid="audit-sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="input-a" />
            </Field>
          </div>
          <Field label="Contexto y objetivo" required>
            <textarea required rows={3} data-testid="audit-context" value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className="input-a resize-none" />
          </Field>
          <div className="mt-4 flex gap-2">
            <button type="submit" disabled={loading} data-testid="audit-create-submit" className="rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {loading ? "Creando…" : "Crear"}
            </button>
            <button type="button" onClick={() => setCreating(false)} className="rounded-md border border-[hsl(var(--audit-border))] px-4 py-2 text-sm">Cancelar</button>
          </div>
          <style>{`.input-a{width:100%;border:1px solid hsl(var(--audit-border));border-radius:0.375rem;padding:0.55rem 0.75rem;font-size:0.875rem;background:white;font-family:inherit}.input-a:focus{outline:none;border-color:hsl(var(--audit-text));box-shadow:0 0 0 3px hsl(0 0% 20%/0.1)}`}</style>
        </form>
      )}

      <div className="mt-8">
        {audits.length === 0 ? (
          <div className="rounded-md border border-dashed border-[hsl(var(--audit-border))] bg-white p-10 text-center text-sm text-[hsl(var(--audit-muted))]">
            Aún no hay auditorías. Crea la primera para comenzar.
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-[hsl(var(--audit-border))] bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-[hsl(var(--audit-border))] bg-[hsl(var(--audit-bg))] font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">
                <tr>
                  <th className="px-4 py-3">Sitio</th>
                  <th className="px-4 py-3">URL</th>
                  <th className="px-4 py-3">Fase</th>
                  <th className="px-4 py-3">Creado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {audits.map((a) => (
                  <tr key={a.id} className="border-b border-[hsl(var(--audit-border))] last:border-0 hover:bg-[hsl(var(--audit-bg))]" data-testid={`audit-row-${a.id}`}>
                    <td className="px-4 py-3 font-semibold">{a.site_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[hsl(var(--audit-muted))]">{a.target_url}</td>
                    <td className="px-4 py-3 font-mono">{Math.min(a.current_phase, 8)}/8</td>
                    <td className="px-4 py-3 text-xs text-[hsl(var(--audit-muted))]">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/audit/${a.id}`} data-testid={`audit-open-${a.id}`} className="inline-flex items-center gap-1 text-sm font-semibold hover:underline">
                        Abrir <ArrowUpRight size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--audit-muted))]">
        {label} {required && "*"}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
